import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { CurrentSection } from "./Captions.tsx";
import { CSSProperties, ReactNode } from "react";
import { CaptionAnimationDto, CaptionDisplayMode } from "@repo/api/caption";
import { msToFrames } from "../../utils/timing.ts";

type RenderWordsProps = {
  displayMode: CaptionDisplayMode;
  currentSection: CurrentSection[];
  textStyles: CSSProperties;
  activeTextStyles: CSSProperties;
  animation?: CaptionAnimationDto;
  containerStyle?: CSSProperties;
};

type SharedDisplayProps = Pick<
  RenderWordsProps,
  "currentSection" | "textStyles" | "animation" | "containerStyle"
>;

type SingleWordProps = SharedDisplayProps;

type HighlightedWordsProps = SharedDisplayProps & {
  activeTextStyles: CSSProperties;
};

type StaticWordsProps = SharedDisplayProps;

function CaptionDisplayWrapper({
  containerStyle,
  animatedStyle,
  children,
}: {
  containerStyle?: CSSProperties;
  animatedStyle?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      className="text-center"
      style={{
        ...containerStyle,
        ...animatedStyle,
      }}
    >
      {children}
    </div>
  );
}

function getSectionAnimationState(
  currentSection: CurrentSection[],
  currentFrame: number,
  fps: number,
  animation?: CaptionAnimationDto,
) {
  const sectionStartTime = currentSection[0]?.startTime ?? 0;
  const sectionStartFrame = Math.round(sectionStartTime * fps);
  const frameSinceEntry = currentFrame - sectionStartFrame;

  const opacity =
    animation?.type === "FADE_IN"
      ? interpolate(frameSinceEntry, [0, msToFrames(250, fps)], [0, 1], {
          extrapolateRight: "clamp",
        })
      : undefined;

  const scale =
    animation?.type === "POP_IN"
      ? spring({
          frame: frameSinceEntry,
          fps,
          config: {
            damping: 12,
            stiffness: 200,
            mass: 0.5,
          },
          from: 0.7,
          to: 1,
        })
      : undefined;

  return { opacity, scale };
}

function getAnimatedStyle({
  opacity,
  scale,
}: {
  opacity?: number;
  scale?: number;
}): CSSProperties {
  return {
    ...(opacity != null && { opacity }),
    ...(scale != null && {
      display: "inline-block",
      transformOrigin: "center bottom",
      transform: `translateZ(0) scale(${scale})`,
      willChange: "transform",
      backfaceVisibility: "hidden",
      verticalAlign: "bottom",
    }),
  };
}

export function SingleWord({
  currentSection,
  textStyles,
  animation,
  containerStyle,
}: SingleWordProps) {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const currentTime = currentFrame / fps;

  let activeWord: string | null = null;
  let activeWordStartTime = 0;

  for (let i = currentSection.length - 1; i >= 0; i--) {
    if (currentTime >= currentSection[i].startTime) {
      activeWord = currentSection[i].word;
      activeWordStartTime = currentSection[i].startTime;
      break;
    }
  }

  if (!activeWord) {
    return null;
  }

  const wordStartFrame = Math.round(activeWordStartTime * fps);
  const frameSinceWordStart = currentFrame - wordStartFrame;

  const opacity = animation
    ? interpolate(frameSinceWordStart, [0, msToFrames(100, fps)], [0, 1], {
        extrapolateRight: "clamp",
      })
    : undefined;

  const scale =
    animation?.type === "POP_IN"
      ? spring({
          frame: frameSinceWordStart,
          fps,
          config: {
            damping: 12,
            stiffness: 200,
            mass: 0.5,
          },
          from: 0.7,
          to: 1,
        })
      : undefined;

  return (
    <CaptionDisplayWrapper
      containerStyle={containerStyle}
      animatedStyle={getAnimatedStyle({ scale, opacity })}
    >
      <span style={textStyles}>{activeWord}</span>
    </CaptionDisplayWrapper>
  );
}

export function HighlightedWords({
  currentSection,
  textStyles,
  activeTextStyles,
  animation,
  containerStyle,
}: HighlightedWordsProps) {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const currentTime = currentFrame / fps;

  const { opacity } = getSectionAnimationState(
    currentSection,
    currentFrame,
    fps,
    animation,
  );

  const textColor = textStyles.color;
  const activeColor = activeTextStyles.color;
  const activeOpacity = activeTextStyles.opacity;

  return (
    <CaptionDisplayWrapper
      containerStyle={containerStyle}
      animatedStyle={getAnimatedStyle({ opacity })}
    >
      {currentSection.map((word, i) => {
        const isActive = currentTime >= word.startTime;

        let wordOpacity: CSSProperties["opacity"] | undefined = isActive
          ? activeOpacity
          : textStyles.opacity;

        if (animation?.type === "FADE_IN" && isActive) {
          const wordStartFrame = Math.round(word.startTime * fps);
          const frameSinceWordStart = currentFrame - wordStartFrame;

          const wordFade = interpolate(
            frameSinceWordStart,
            [0, msToFrames(200, fps)],
            [0, 1],
            {
              extrapolateRight: "clamp",
            },
          );

          wordOpacity =
            typeof activeOpacity === "number"
              ? activeOpacity * wordFade
              : wordFade;
        }

        return (
          <span
            key={i}
            style={{
              ...textStyles,
              color: isActive ? activeColor : textColor,
              opacity: wordOpacity,
            }}
          >
            {word.word}{" "}
          </span>
        );
      })}
    </CaptionDisplayWrapper>
  );
}

export function StaticWords({
  currentSection,
  textStyles,
  animation,
  containerStyle,
}: StaticWordsProps) {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();

  const { opacity, scale } = getSectionAnimationState(
    currentSection,
    currentFrame,
    fps,
    animation,
  );

  return (
    <CaptionDisplayWrapper
      containerStyle={containerStyle}
      animatedStyle={getAnimatedStyle({ opacity, scale })}
    >
      <span style={textStyles}>
        {currentSection.reduce((acc, word) => acc + word.word + " ", "")}
      </span>
    </CaptionDisplayWrapper>
  );
}

export function RenderWords({
  displayMode,
  currentSection,
  textStyles,
  activeTextStyles,
  animation,
  containerStyle,
}: RenderWordsProps) {
  switch (displayMode) {
    case "SINGLE_WORD":
      return (
        <SingleWord
          currentSection={currentSection}
          textStyles={textStyles}
          animation={animation}
          containerStyle={containerStyle}
        />
      );
    case "HIGHLIGHTED":
      return (
        <HighlightedWords
          currentSection={currentSection}
          textStyles={textStyles}
          activeTextStyles={activeTextStyles}
          animation={animation}
          containerStyle={containerStyle}
        />
      );
    case "STATIC":
    default:
      return (
        <StaticWords
          currentSection={currentSection}
          textStyles={textStyles}
          animation={animation}
          containerStyle={containerStyle}
        />
      );
  }
}
